// Кошелёк TON: подключение через TON Connect и доказательство владения (`ton_proof`), этап Э1.
//
// Что здесь происходит по шагам:
//   1. спрашиваем у сервера параметры (`GET /ton/config`) — адрес манифеста и возврат в мини-апп;
//   2. берём одноразовый код (`POST /ton/payload`) и кладём его в `tonProof`;
//   3. игрок подтверждает подключение в кошельке;
//   4. отдаём ответ кошелька серверу (`POST /ton/link`), он проверяет подпись и привязывает адрес.
//
// 🔴 БЕЗ ШАГА 4 ПРИВЯЗКИ НЕТ. Адрес, который вернул кошелёк, — просто строка в браузере: доверять
// ей нельзя, владение доказывает только подпись, проверенная на сервере.
//
// 🔴 Библиотека грузится ЛЕНИВО (`import()` внутри функции): она весит заметно и нужна одному блоку
// в профиле — тянуть её в общий бандл ради кнопки, которую нажимают раз в жизни, незачем.

import axios from 'axios';

import { isTelegram } from '@/telegram';
import { connectForSend, dropConnection, ensureWallet, runConnect, sendWithTimeout } from '@/tonFlow';

let uiPromise = null;   // единственный экземпляр TonConnectUI на вкладку
let cachedConfig = null;

/** Параметры подключения с сервера (кэшируем: они не меняются в течение сессии). */
export async function tonConfig() {
  if (cachedConfig) return cachedConfig;
  const { data } = await axios.get('/ton/config');
  cachedConfig = data || {};
  return cachedConfig;
}

/** Раздел кошелька включён на сервере? (нет адреса коллекции → блок не рисуем вовсе) */
export async function tonEnabled() {
  try {
    return !!(await tonConfig()).enabled;
  } catch (e) {
    return false;
  }
}

async function getUi() {
  if (uiPromise) return uiPromise;
  uiPromise = (async () => {
    const cfg = await tonConfig();
    const { TonConnectUI } = await import(/* webpackChunkName: "tonconnect" */ '@tonconnect/ui');
    const options = {
      manifestUrl: cfg.manifest_url || `${window.location.origin}/tonconnect-manifest.json`,
      // 🔴 По умолчанию библиотека шлёт СВОЮ телеметрию — выключаем: игрок не подписывался на
      // отправку своих действий третьей стороне, а мы про эту отправку узнали бы последними.
      analytics: { mode: 'off' },
    };
    // 🔴 В мини-аппе без `twaReturnUrl` игрока НЕ ВЕРНЁТ в игру после подтверждения в кошельке:
    // он останется в кошельке, а мы будем ждать ответа, которого он не увидит.
    if (isTelegram() && cfg.twa_return_url) {
      options.actionsConfiguration = { twaReturnUrl: cfg.twa_return_url };
    }
    return new TonConnectUI(options);
  })();
  return uiPromise;
}

/**
 * Подключить кошелёк и привязать его к аккаунту.
 * Возвращает состояние привязки с сервера ({linked, address, short, linked_at}).
 */
export async function connectWallet() {
  const ui = await getUi();
  // Порядок шагов и все выходы по времени — в `@/tonFlow` (там же объяснение, почему без них
  // кнопка привязки «перестаёт работать» после любого сбоя).
  return runConnect({
    ui,
    requestPayload: async () => (await axios.post('/ton/payload')).data.payload,
    submitProof: async (wallet, proof) => (await axios.post('/ton/link', {
      address: wallet.account.address,
      network: wallet.account.chain,
      proof,
    })).data,
  });
}

/** Отвязать кошелёк от аккаунта (и отключить его в самой библиотеке). */
export async function disconnectWallet() {
  try {
    // 🔴 Ждём разрыв не дольше своего потолка: открытое приложение кошелька может держать мост,
    // а привязку в игре снимать всё равно надо.
    await dropConnection(await getUi());
  } catch (e) { /* библиотека могла и не загрузиться — привязку всё равно снимаем */ }
  const { data } = await axios.delete('/ton/link');
  return data;
}

/**
 * Запечь комплект NFT-фигуры (платно) или снять печать («Разблокировать» / «Забрать в игру»).
 * Возвращает отчёт сервера: {price, items, card_updated}.
 */
export async function bakeNft(unitId, unbake = false) {
  const { data } = await axios.post(unbake ? '/ton/unbake' : '/ton/bake', { unit_id: unitId });
  return data;
}

/** Состояние привязки текущего игрока. */
export async function walletStatus() {
  const { data } = await axios.get('/ton/status');
  return data;
}

/** Баланс $ENRG и NFT нашей коллекции на привязанном кошельке. */
export async function walletAssets(force = false) {
  const { data } = await axios.get('/ton/assets', { params: force ? { force: 1 } : {} });
  return data;
}

/** Игрок прочитал отчёт сверки владения («фигура ушла с проданной NFT») — гасим его. */
export async function clearWalletNotices() {
  const { data } = await axios.delete('/ton/notices');
  return data;
}

// ─── ОБМЕН $ENRG НА ЭНЕРГИЮ (web3 Э4) ──────────────────────────────────────────────────────────

/** Настройки обмена и последние зачисления игрока. */
export async function exchangeStatus() {
  const { data } = await axios.get('/ton/exchange');
  return data;
}

/**
 * Обменять `amount` токенов: сервер собирает операцию сжигания, кошелёк её подписывает.
 *
 * 🔴 ПОДПИСЬ — ЭТО НЕ ЗАЧИСЛЕНИЕ. Кошелёк отвечает «отправлено», а энергия появляется, когда
 * транзакцию увидит блокчейн, — поэтому дальше экран сам спрашивает сервер (`collectExchange`).
 * Обещать энергию по ответу кошелька нельзя: сообщение может и не дойти.
 *
 * 🔴 Тело операции собирает СЕРВЕР: правила обмена не должны жить в браузере, где их нечем проверить.
 */
export async function burnTokens(amount) {
  const { data } = await axios.post('/ton/exchange/prepare', { amount });
  const ui = await getUi();
  // 🔴 СНАЧАЛА УБЕЖДАЕМСЯ, ЧТО КОШЕЛЁК ПОДКЛЮЧЁН В БИБЛИОТЕКЕ. Привязка в игре живёт на сервере, а
  // мост TON Connect рвётся сам (закрыли браузер, истекла сессия) — и тогда `sendTransaction` просто
  // бросает «кошелёк не подключён», что с экрана выглядит как «кнопка ничего не делает».
  const wallet = await ensureWallet(ui);
  const connected = (wallet && wallet.account && wallet.account.address) || (ui.account && ui.account.address) || '';
  // 🔴 Подписать операцию может ЛЮБОЙ подключённый кошелёк, но сгорят тогда токены ЧУЖОГО кошелька,
  // а энергию мы начислим владельцу привязки. Сверяем адреса и отказываем понятным текстом.
  if (data.wallet && connected && String(connected).toLowerCase() !== String(data.wallet).toLowerCase()) {
    throw new Error('wallet-mismatch');
  }
  await sendWithTimeout(ui, {
    // Пять минут на подпись — с запасом: игрок может медленно вводить пин или искать телефон.
    validUntil: Math.floor(Date.now() / 1000) + 300,
    messages: [{ address: data.to, amount: data.amount, payload: data.payload }],
  });
  return data;
}

/**
 * Что библиотека думает про кошелёк ПРЯМО СЕЙЧАС: `{connected, address}`.
 *
 * 🔴 Нужна как ВИДИМАЯ ДИАГНОСТИКА в окне обмена. Три состояния кошелька (привязка на сервере, мост
 * в браузере, подпись) снаружи неразличимы, и без этой строки и игрок, и мы гадаем, что происходит:
 * «мигает окно» может означать и мёртвый мост, и отказ кошелька, и отмену.
 */
export async function walletBridge() {
  try {
    const ui = await getUi();
    if (ui.connectionRestored) await ui.connectionRestored;
    const account = (ui.wallet && ui.wallet.account) || ui.account || null;
    return { connected: !!account, address: (account && account.address) || '' };
  } catch (e) {
    return { connected: false, address: '' };
  }
}

/**
 * Подключить кошелёк для ПОДПИСИ операции — отдельным действием игрока.
 *
 * 🔴 Отдельная кнопка, а не шаг внутри обмена (жалоба оунера 2026-08-23: «Тонкипер предложил
 * привязать кошелёк, а не подписать транзакцию»). Игрок должен понимать, что именно он сейчас
 * подтверждает: подключение браузера к кошельку или списание токенов.
 */
export async function connectWalletForSend() {
  const ui = await getUi();
  const wallet = await connectForSend(ui);
  return (wallet && wallet.account && wallet.account.address) || '';
}

/** Спросить сервер, не пришли ли сожжённые токены (зовём несколько раз после подписи). */
export async function collectExchange() {
  const { data } = await axios.post('/ton/exchange/collect');
  return data;
}

/**
 * Сказать серверу, что игрок ПОДПИСАЛ сжигание и ждёт зачисления.
 *
 * 🔴 Зовётся сразу после ответа кошелька, до всякого опроса. Между подписью и появлением транзакции
 * в блокчейне проходят минуты: если игрок закроет вкладку (а он закроет — ждать никто не любит),
 * без этой отметки от операции не остаётся НИ СЛЕДА, и энергия потом приходит молча. Отметка живёт
 * на сервере и гаснет сама при зачислении, поэтому её потеря ничего не ломает — молчим об ошибке.
 */
export async function markExchangeSent(amount) {
  try {
    await axios.post('/ton/exchange/sent', { amount });
  } catch (e) { /* отметка вторична: операция уже ушла в сеть */ }
}
